import { createContext, ReactNode, useContext, useMemo } from 'react'

import {
  HivAnamnesisFormSection,
  HivAnamnesisPrefillSource
} from 'src/api/requests'

type HivAnamnesisFormContextValue = {
  /**
   * Where a prefilled field came from. A source marks its field and the field's children, e.g.
   * `testing.firstScreening` marks `testing.firstScreening.date`.
   */
  getSource: (field: string) => HivAnamnesisPrefillSource | undefined
  /** False when the user cannot see the patient's names and IDNP (they are hidden by the API). */
  userHasAccessToPersonalData: boolean
  /** The form is being edited (the "Preia din sistem" buttons are shown only then). */
  isEditing: boolean
  /** Fills sections from the system data, keeping what the doctor entered where the system has nothing. */
  refreshFromSystem?: (sections: HivAnamnesisFormSection[]) => Promise<void>
}

// `family.children[0].result` → `family.children.0.result`, the form field path.
const normalize = (field: string) => field.replace(/\[(\d+)\]/g, '.$1')

const HivAnamnesisFormContext = createContext<HivAnamnesisFormContextValue>({
  getSource: () => undefined,
  userHasAccessToPersonalData: true,
  isEditing: false
})

type Props = {
  sources?: HivAnamnesisPrefillSource[] | null
  userHasAccessToPersonalData: boolean
  isEditing?: boolean
  refreshFromSystem?: (sections: HivAnamnesisFormSection[]) => Promise<void>
  children: ReactNode
}

export const HivAnamnesisFormProvider = ({
  sources,
  userHasAccessToPersonalData,
  isEditing = false,
  refreshFromSystem,
  children
}: Props) => {
  const value = useMemo(() => {
    const byField = new Map(
      (sources ?? [])
        .filter((source) => !!source.field)
        .map((source) => [normalize(String(source.field)), source])
    )

    const getSource = (field: string) => {
      const parts = field.split('.')
      while (parts.length) {
        const source = byField.get(parts.join('.'))
        if (source) return source
        parts.pop()
      }

      return undefined
    }

    return {
      getSource,
      userHasAccessToPersonalData,
      isEditing,
      refreshFromSystem
    }
  }, [sources, userHasAccessToPersonalData, isEditing, refreshFromSystem])

  return (
    <HivAnamnesisFormContext.Provider value={value}>
      {children}
    </HivAnamnesisFormContext.Provider>
  )
}

export const useHivAnamnesisForm = () => useContext(HivAnamnesisFormContext)
